import React, { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { clsx } from "clsx";
import { TenantHeader } from "./TenantHeader";
import { TenantSidebar } from "./TenantSidebar";
import { navKeyForPath } from "./navConfig";
import { useSidebar } from "@stores/sidebar/useSidebar";
import { useAuthStore } from "@stores/auth/authStore";
import { useScrollToTop } from "@hooks/useScrollToTop";
import { useServerSettingsSync } from "@features/settings/useTenantSettings";
import { ErrorBoundary } from "@components/ErrorBoundary";
import { NotificationStreamProvider } from "@components/notifications/NotificationStreamProvider";

/** Shown on every screen while the account is locked for non-payment. */
const BillingLockedBanner: React.FC = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    return (
        <div
            role="alert"
            className="mb-6 flex flex-wrap items-start gap-3 rounded-xl border border-amber-300/60 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100"
        >
            <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
                <p className="font-semibold">{t("billing.locked.title")}</p>
                <p className="mt-0.5 opacity-90">{t("billing.locked.body")}</p>
            </div>
            {pathname !== "/subscription" && (
                <Link to="/subscription" className="font-semibold underline">
                    {t("billing.locked.action")}
                </Link>
            )}
        </div>
    );
};

export const TenantLayout: React.FC = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { isCollapsed, toggleCollapse } = useSidebar();
    const { pathname } = useLocation();
    const billingOnly = useAuthStore((s) => s.billingOnly);
    useScrollToTop({ pathname });
    useServerSettingsSync();

    const { t, i18n } = useTranslation();
    const navKey = navKeyForPath(pathname);
    React.useEffect(() => {
        const brand = t("common.brand");
        document.title = navKey ? `${t(`nav.${navKey}`)} · ${brand}` : brand;
    }, [navKey, i18n.language, t]);

    return (
        <NotificationStreamProvider>
            <div className="min-h-screen bg-gradient-to-br from-neutral-light via-white to-neutral-light/50 transition-colors duration-300 dark:from-neutral-dark dark:via-neutral-dark dark:to-neutral-dark/80">
                <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(31,182,255,0.03),transparent_50%)] dark:bg-[radial-gradient(circle_at_50%_50%,rgba(31,182,255,0.05),transparent_50%)]" />
                <TenantHeader onMobileMenuToggle={() => setIsMobileMenuOpen((o) => !o)} />
                <div className="relative flex">
                    <TenantSidebar
                        isMobileOpen={isMobileMenuOpen}
                        onMobileClose={() => setIsMobileMenuOpen(false)}
                        isCollapsed={isCollapsed}
                        onToggleCollapse={toggleCollapse}
                    />
                    <main
                        className={clsx(
                            "relative z-10 min-h-[calc(100vh-4rem)] min-w-0 flex-1 px-4 pb-12 pt-6 transition-all duration-300 sm:px-6 lg:px-8 xl:px-10",
                            isCollapsed ? "lg:ms-16" : "lg:ms-60"
                        )}
                    >
                        <div className="mx-auto max-w-7xl">
                            {billingOnly && <BillingLockedBanner />}
                            <ErrorBoundary key={pathname}>
                                <Outlet />
                            </ErrorBoundary>
                        </div>
                    </main>
                </div>
            </div>
        </NotificationStreamProvider>
    );
};
